import findIdsOnLoops from './findIdsOnLoops';

const GONE_SOURCE_STATUSES = new Set(['disabled', 'archived']);

/**
 * The ticket types a seller's rules hide: `IS` shows a ticket type while any
 * named source is selected, `IS_NOT` while none is. Display only - the server
 * never evaluates these rules.
 *
 * A source hidden by its own rule counts as not selected, so chains collapse
 * from the root. Ticket types on a loop of rules are hidden, as order bumps
 * are. A source missing from the page, disabled or archived can never be
 * selected, so its rule stays in force, as variant rules do. A rule with no
 * ticket picked yet hides nothing.
 *
 * `ticketTypes` is every ticket type on the page, flattened across groups.
 * `selectedQuantities` is `{ [ticketTypeId]: quantity }`.
 */
export default function getTicketTypesHiddenByRules({ ticketTypes, selectedQuantities }) {
  const byId = new Map((ticketTypes ?? []).map(ticketType => [String(ticketType._id), ticketType]));

  const namedIdsOf = id => {
    const logic = byId.get(id)?.showHideLogic;
    if (!logic?.enabled) return [];

    return [...new Set((logic.ticketTypeIds ?? []).map(String))].filter(
      sourceId => sourceId !== id,
    );
  };

  const isLive = sourceId => {
    const source = byId.get(sourceId);
    return source != null && !GONE_SOURCE_STATUSES.has(source.status);
  };

  const sourceIdsOf = id => namedIdsOf(id).filter(isLive);

  const loopedIds = findIdsOnLoops([...byId.keys()], sourceIdsOf);
  const hiddenById = new Map();

  const isSelected = id => !isHidden(id) && Number(selectedQuantities?.[id]) > 0;

  const decide = id => {
    if (loopedIds.has(id)) return true;

    if (!namedIdsOf(id).length) return false;

    const anySelected = sourceIdsOf(id).some(isSelected);
    const comparison = byId.get(id).showHideLogic.comparison;

    return comparison === 'IS_NOT' ? anySelected : !anySelected;
  };

  // Nothing off a loop leads back to itself, so this recursion always ends.
  const isHidden = id => {
    if (!hiddenById.has(id)) hiddenById.set(id, decide(id));
    return hiddenById.get(id);
  };

  return new Set([...byId.keys()].filter(isHidden));
}
